import React from 'react';
import { ClinicalVisuals } from './ClinicalVisuals';
import { defineMessages, useIntl } from 'react-intl';
import { Info } from '@lifeomic/chromicons';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { useActiveProject } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProject';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks/useNotify';
import SummaryService from '@lifeomic/phc-web-toolkit/src/services/SummaryService';
import TableTitle from '@lifeomic/phc-web-toolkit/src/components/Table/TableTitle';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { useActiveProjectCohortId } from '@lifeomic/phc-web-toolkit/src/hooks';

const summaryService = new SummaryService();

const messages = defineMessages({
  clinicalTitle: {
    id: 'pages.summary.clinical.title',
    defaultMessage: 'Clinical',
  },
  clinicalInfoTooltip: {
    id: 'pages.summary.clinical.info',
    defaultMessage:
      'The values displayed here are clinical summaries for patient data loaded into the project.',
  },
  fetchFailureToast: {
    id: 'pages.summary.data.fetchFailureToast',
    defaultMessage: 'An error occurred when fetching summary data',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
    '& + $root': {
      marginTop: theme.spacing(4),
    },
    '&:last-of-type': {
      marginBottom: theme.spacing(4),
    },
  },
  sectionTitle: {
    '& > div > div > p': {
      color: theme.palette.text.primary,
      fontSize: theme.typography.body1.fontSize,
    },
  },
}));

const appendPatientCount = (message: any, resources: any) => {
  if (resources.isLoading || !resources || !resources.clinical) {
    return message;
  }

  const count = resources.clinical.patient.count;
  return {
    id: `${message.id}.count`,
    defaultMessage: `${message.defaultMessage} (${count} Patients)`,
  };
};

export const ProjectData = () => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const [activeProject] = useActiveProject();
  const activeProjectCohortId = useActiveProjectCohortId();
  const [getResources, getResourcesState] = useService(
    summaryService.getAllProjectSummaries,
  );

  const [addNote] = useNotify();

  const [resources, setResources] = React.useState([]);

  React.useEffect(() => {
    if (!activeProject) {
      return;
    }
    getResources(activeProject.id, activeProjectCohortId);
  }, [activeProject, activeProjectCohortId, getResources]);

  React.useEffect(() => {
    if (!getResourcesState.response) {
      return;
    }

    const { data } = getResourcesState.response;
    setResources(data);
  }, [getResourcesState.response]);

  React.useEffect(() => {
    if (getResourcesState.error) {
      addNote(formatMessage(messages.fetchFailureToast), 'error');
    }
  }, [addNote, formatMessage, getResourcesState.error]);

  return (
    <div>
      <Paper className={classes.root} padding={0}>
        <TableTitle
          className={classes.sectionTitle}
          title={formatMessage(
            appendPatientCount(messages.clinicalTitle, resources),
          )}
          rightContent={
            <Tooltip
              title={formatMessage(messages.clinicalInfoTooltip)}
              placement="top"
            >
              <span>
                <Info width={20} height={20} />
              </span>
            </Tooltip>
          }
        >
          <ClinicalVisuals
            resources={resources}
            isLoading={getResourcesState.isFetching}
          />
        </TableTitle>
      </Paper>
    </div>
  );
};
